import React from 'react';
import { CheckoutForm, PremiumPageUpgradePaymentConfirmation } from '@gik/checkout';
import ModalButtonFooter from '@gik/ui/Modal/ModalButtonFooter';
import ModalHeader from '@gik/ui/Modal/ModalHeader';
import Modal from '@gik/ui/Modal';
import { ModalPage } from '@gik/ui/Modal/models/ModalPage';
import { CheckoutFormValues, CheckoutFormPayload } from './CheckoutForm';
import PaymentConfirmation from '../PaymentConfirmation/PaymentConfirmation';
import { PaymentConfirmationValues, CheckoutFormInitiatedOnType } from '@gik/checkout/types';
import { gikClassPrefix } from '@gik/core/constants';
import { Button } from '@gik/ui';
import { Analytics } from '@gik/core/utils';
import { navigateTo } from '@/utils/environment';
import { fireCompletedPurchaseLocalStorageKey } from '@gik/inkind-page/components/InkindPageViewRecorder/useCompletedPurchaseForPageAnalytics';
import { StripeCardElement } from '@stripe/stripe-js';

export interface CheckoutFormModalProps {
  /**
   * Render Shipping information in order summary
   */
  shipping?: boolean;

  /**
   * Allow user to enter shipping details (disabled for purchasing for inkind pages)
   */
  shippingEditable?: boolean;

  /**
   * Allow a shipping option to be selected (standard, fast delivery)
   */
  hasShipping?: boolean;

  /**
   * Skip the products step when first opened (product/wishlist/giftbox page)
   */
  skipProducts?: boolean;
  hideProducts?: boolean;

  showModal?: boolean;

  disableBackdrop?: boolean;

  errorMessage?: string;

  paymentConfirmationValues?: PaymentConfirmationValues;

  initialData?: CheckoutFormValues;

  testImageFallback?: boolean;

  initiatedOn: CheckoutFormInitiatedOnType;
  inkindRouteId?: string;

  navigateToInkindPageAfterPurchase?: boolean;

  onSubmit?(
    values: CheckoutFormPayload,
    updateCheckoutStatusText: (text: string) => void
  ): void | boolean | Promise<boolean> | Promise<void>;
  onChange?(values: CheckoutFormValues): void;
  onClose?(): void;
  closeModal?(): void;
}

export interface ICheckoutFormModalContext {
  cardElement?: StripeCardElement;
  setCardElement?(cardElement: StripeCardElement): void;
  modalContentRef: React.MutableRefObject<HTMLDivElement>;
}

export const CheckoutFormModalContext = React.createContext<ICheckoutFormModalContext>(null);

export default function CheckoutFormModal({
  shipping,
  shippingEditable,
  hasShipping,
  skipProducts,
  hideProducts,
  showModal,
  initialData,
  errorMessage,
  paymentConfirmationValues,
  initiatedOn,
  inkindRouteId,
  disableBackdrop,
  navigateToInkindPageAfterPurchase = true,
  onClose,
  onSubmit,
  onChange,
}: CheckoutFormModalProps): React.ReactElement {
  const [stepsPortal, setStepsPortal] = React.useState(undefined);
  const [buttonsPortal, setButtonsPortal] = React.useState(undefined);

  const modalContentRef = React.useRef<HTMLDivElement>(null);

  const handleOnClose = () => {
    if (paymentConfirmationValues) {
      Analytics.fireEvent('CompletedPurchaseCloseModal', {});
    }
    onClose();
  };

  const handleDone = () => {
    if (inkindRouteId && navigateToInkindPageAfterPurchase) {
      // TODO: after SPA migration remove the below line + useCompletedPurchaseForPageAnalytics as SPA redirection will not interrupt events
      localStorage.setItem(fireCompletedPurchaseLocalStorageKey, 'true');
      navigateTo(`/inkinds/${inkindRouteId}`);
    } else {
      handleOnClose();
    }
  };

  function handleGotoStep() {
    if (modalContentRef && modalContentRef.current) modalContentRef.current.scrollTop = 0;
  }

  const modalFooter = (
    <ModalButtonFooter
      buttons={[
        <div key="ref" ref={el => setButtonsPortal(el)} />,
        paymentConfirmationValues ? (
          <Button key="btn-confirmation-done" variant="primary" onClick={handleDone}>
            Done
          </Button>
        ) : null,
      ]}
    />
  );

  const headerContent = (
    <div>
      {stepsPortal && (
        <CheckoutForm
          onSubmit={onSubmit}
          onChange={onChange}
          onGotoStep={handleGotoStep}
          initialData={initialData}
          shipping={shipping}
          shippingEditable={shippingEditable}
          hasShipping={hasShipping}
          skipProducts={skipProducts}
          hideProducts={hideProducts}
          errorMessage={errorMessage}
          stepsPortal={stepsPortal}
          buttonsPortal={buttonsPortal}
          initiatedOn={initiatedOn}
          inkindRouteId={inkindRouteId}
        />
      )}
    </div>
  );

  if (!initialData) {
    return null;
  }

  const blockName = `${gikClassPrefix}-checkout-form-modal`;

  const modalContent = <div className={`${blockName}__content`} ref={el => setStepsPortal(el)}></div>;

  const modalTitle = initialData.cart?.some(product => product.checkoutType === 'tango-card')
    ? 'Send Gift Card'
    : initialData.cart?.some(product => product.checkoutType === 'giftbox')
    ? 'Send Gift Box'
    : initialData.cart?.some(product => product.checkoutType === 'gik-premium')
    ? 'Upgrade Your Page'
    : `Send ${initialData.cart?.[0]?.name}`; // virtual product

  const modalHeader = <ModalHeader onClose={handleOnClose} title={modalTitle} content={headerContent} />;

  const pages: ModalPage[] = [
    {
      content: modalContent,
    },
    {
      content: (
        <div className={`${blockName}__content-confirmation`}>
          {paymentConfirmationValues &&
            (paymentConfirmationValues.products[0].checkoutType === 'gik-premium' ? (
              <PremiumPageUpgradePaymentConfirmation
                productName={paymentConfirmationValues.productName}
                values={paymentConfirmationValues}
              />
            ) : (
              <PaymentConfirmation
                productName={paymentConfirmationValues.productName}
                values={paymentConfirmationValues}
                shipping={shipping}
              />
            ))}
        </div>
      ),
    },
  ];

  return (
    <CheckoutFormModalContext.Provider
      value={{
        modalContentRef,
      }}
    >
      <Modal
        className="gik-modal-checkout"
        disableBackdrop={disableBackdrop}
        shouldCloseOnEsc={false}
        isOpen={showModal}
        onClose={handleOnClose}
        header={modalHeader}
        footer={modalFooter}
        pageNumber={paymentConfirmationValues ? 2 : 1}
        pages={pages}
        contentRef={modalContentRef}
      />
    </CheckoutFormModalContext.Provider>
  );
}
